/* Session count behind the header's personalization bar.
 *
 * Deliberately does NOT go through useFilters: every other read on this page is
 * windowed by the "Last 7 days" picker, and a personalization level that drops
 * to Getting started because someone looked at the last hour would be reporting
 * the picker, not the agent. This hook pins its own fixed trailing window
 * (PERSONALIZATION_WINDOW_DAYS) so the level is stable whatever the page shows.
 *
 * One read, agent-scoped server-side (useScopedQuery only injects agent_id when
 * no override is passed, so the override carries it explicitly):
 *   /agents -> session_count
 */

import { useMemo } from "react";

import { DAY_MS, dashboardNow, dateOnly } from "../../analytics/clock";
import { useAgentRollups } from "../../analytics/hooks";
import {
  PERSONALIZATION_WINDOW_DAYS,
  resolveLevel,
  type PersonalizationLevel,
  type PersonalizationSignals,
} from "./personalizationThresholds";

export interface PersonalizationState {
  /** Null until the read settles — hold the UI rather than paint a Getting
   *  started that upgrades a moment later. */
  level: PersonalizationLevel | null;
  signals: PersonalizationSignals | null;
}

export function usePersonalizationSignals(
  agentId: string,
): PersonalizationState {
  // Date-only bounds (whole UTC days), matching the day-grained presets in
  // analytics/filters — so the key is stable within a day and the gateway reads
  // each bound as a full day.
  const query = useMemo(() => {
    const to = dashboardNow();
    const from = new Date(to.getTime() - PERSONALIZATION_WINDOW_DAYS * DAY_MS);
    return { from: dateOnly(from), to: dateOnly(to), agent_id: [agentId] };
  }, [agentId]);

  const rollups = useAgentRollups(query);

  if (rollups.data === undefined) {
    return { level: null, signals: null };
  }

  // No rollup row means no traffic in the window, not an error — zero sessions,
  // which resolves to the first level.
  const row = rollups.data.data.items.find((a) => a.agent_id === agentId);
  const signals: PersonalizationSignals = {
    sessions: row?.session_count ?? 0,
  };

  return { level: resolveLevel(signals), signals };
}
